// The citations an artifact carries (#11975): every artifact pane states
// how many distinct sources its content cites, in the header, with the
// same list the chat evidence footer opens — each row opening its source
// in the document panel. Cited table cells wear the chat citation chip.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { timeAgo } from "@/components/FpPages/Documents/browser/places";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Files } from "lucide-react";
import { Tooltip } from "@mui/material";

import { ChatCitationSource } from "../FpPages/Chat/types";
import {
  EvidenceSource,
  chatSourceCalculation,
  chatSourceKind,
  chatSourceLabel,
  citationNumbers,
  collectSources,
  evidenceSources,
} from "../FpPages/Chat/evidenceFooter";
import SourcesPopover from "../FpPages/Chat/components/SourcesPopover";
import { ChatCitationButton } from "../common/MarkDownRenderer";
import {
  openChatCitation,
  type ChatCitationTarget,
} from "../common/openChatCitation";
import {
  fetchArtifactItem,
  type ArtifactSourceKind,
} from "../FpPages/Artifacts/useArtifacts";
import { passageSpelling } from "./passageSpelling";
import { isFigure } from "./citedTableCells";
import { toConversationSlug } from "../FpPages/Chat/chatUtils";
import type { TableColumn } from "./tableArtifact";

/** The path segment right after `name` (the last one so named), or null:
 *  an exact segment, so one id never matches as a prefix of another. */
const segmentAfter = (pathname: string, name: string): string | null => {
  const segments = pathname.split("/");
  const at = segments.lastIndexOf(name);
  return at >= 0 && at + 1 < segments.length ? segments[at + 1] : null;
};

/** Whether the pane is on the conversation that produced the artifact: the
 *  chat it was made in, or the agent run conversation that made it. The
 *  composer under it already talks about the artifact, so Chat has nowhere
 *  else to go (#12481, Gerry: "if you are in chat, shouldn't have the edit
 *  in chat option"; #13261 extends it to the run conversation). The origin
 *  label below says "this" on exactly the same routes. */
export const artifactIsOnItsChat = (
  citations: Pick<ArtifactCitations, "sourceKind" | "sourceConversationId">,
  pathname: string
): boolean => {
  const conversationId = citations.sourceConversationId;
  if (!conversationId) return false;
  switch (citations.sourceKind) {
    case "chat":
      // The chat URL carries the conversation's slug, whichever form of
      // its id the artifact names.
      return (
        segmentAfter(pathname, "chat") === toConversationSlug(conversationId)
      );
    case "agent_run":
      return segmentAfter(pathname, "conversations") === conversationId;
    case "user":
    case null:
      return false;
    default: {
      const unhandled: never = citations.sourceKind;
      return unhandled;
    }
  }
};

/**
 * Where the artifact came from, for the head of the title's menu (#12481,
 * #13261): "From this chat" / "From this run" on the conversation that
 * produced it, else the kind of place it came from; nothing for an upload
 * or before the read lands.
 */
export const artifactOriginLabel = (
  citations: Pick<ArtifactCitations, "sourceKind" | "sourceConversationId">,
  pathname: string
): string | null => {
  const here = artifactIsOnItsChat(citations, pathname);
  switch (citations.sourceKind) {
    case "chat":
      return here ? "From this chat" : "From a chat";
    case "agent_run":
      return here ? "From this run" : "From an agent run";
    case "user":
    case null:
      return null;
    default: {
      const unhandled: never = citations.sourceKind;
      return unhandled;
    }
  }
};

export type ArtifactSourceMap = Record<string, ChatCitationSource[]>;

export interface ArtifactCitations {
  sourceMap: ArtifactSourceMap | null;
  citationScope: string | null;
  /** Where the artifact came from (#12481), off the same read: the header
   *  names it beside the title. Null until the read lands. */
  sourceKind: ArtifactSourceKind | null;
  sourceConversationId: string | null;
  /** When the artifact last changed, for the title menu's head. */
  updatedAt: string | null;
}

const NONE: ArtifactCitations = {
  sourceMap: null,
  citationScope: null,
  sourceKind: null,
  sourceConversationId: null,
  updatedAt: null,
};

export const artifactItemKey = (artifactId: string) =>
  ["artifactItem", artifactId] as const;

/** The single-artifact read every pane shares (#12481): one react-query
 *  read per artifact, whoever asks. */
export const useArtifactItem = (artifactId: string | null | undefined) =>
  useQuery({
    queryKey: artifactItemKey(artifactId ?? ""),
    queryFn: () => fetchArtifactItem(artifactId ?? ""),
    enabled: Boolean(artifactId),
    retry: false,
  });

/**
 * The current version's map and scope for an artifact, and its origin,
 * read off the single-artifact endpoint (listings carry neither). One
 * react-query read per artifact (#12481): a thread of cards and the pane
 * beside it share it. `refreshKey` bumps whenever the content changed under
 * the pane — a chat edit landing, a save — and re-reads it, so the header
 * count follows the content.
 */
export const useArtifactCitations = (
  artifactId: string | null | undefined,
  refreshKey: unknown = null
): ArtifactCitations => {
  const queryClient = useQueryClient();
  const query = useArtifactItem(artifactId);
  // The first render with a key reads from the cache or mounts the query;
  // every later change of the key is content that moved under the pane.
  const seenKeyRef = useRef(refreshKey);
  useEffect(() => {
    if (Object.is(seenKeyRef.current, refreshKey)) return;
    seenKeyRef.current = refreshKey;
    if (!artifactId) return;
    void queryClient.invalidateQueries({
      queryKey: artifactItemKey(artifactId),
    });
  }, [artifactId, queryClient, refreshKey]);
  const item = query.data;
  return useMemo(
    () =>
      item
        ? {
            sourceMap: item.source_map ?? null,
            citationScope: item.citation_scope ?? null,
            sourceKind: item.source_kind,
            sourceConversationId: item.source_conversation_id,
            updatedAt: item.updated_at,
          }
        : NONE,
    [item]
  );
};

/** "Updated 3 hours ago", for the title menu's head (#12481); null until
 *  the item read lands. */
export const artifactUpdatedLabel = (
  citations: Pick<ArtifactCitations, "updatedAt">
): string | null => {
  if (!citations.updatedAt) return null;
  const ago = timeAgo(citations.updatedAt);
  return ago ? `Updated ${ago}` : null;
};

/**
 * The distinct sources an artifact cites, numbered in the map's order —
 * the display order the backend stamped when it resolved the content.
 */
export const artifactSources = (
  sourceMap: ArtifactSourceMap | null | undefined
): EvidenceSource[] =>
  sourceMap ? collectSources([{ text: "", map: sourceMap }]) : [];

interface ArtifactSourcesButtonProps {
  sourceMap: ArtifactSourceMap | null | undefined;
  /** The scope the artifact's citations resolve against; the structured
   *  data viewer keys its session on it. */
  citationScope?: string | null;
}

/** The chip that names how many sources something cites (#12481): bordered,
 *  led by a document glyph, opening the list on click. The pane header and
 *  the answer footer wear the same one. */
export const SourcesChip = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    clause: string;
    /** What leads the label: the default glyph, or a stack of type tiles. */
    lead?: React.ReactNode;
    open: boolean;
  }
>(function SourcesChip({ clause, lead, open, className, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      aria-haspopup="dialog"
      aria-expanded={open}
      className={`inline-flex h-[24px] flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-[5px] border bg-[var(--fp-surface)] px-2 text-[11.5px] font-medium text-[var(--fp-ink)] transition-colors ${
        open
          ? "border-[var(--fp-ink)]"
          : "border-[var(--fp-border-strong)] hover:border-[var(--fp-ink)]"
      } ${className ?? ""}`}
      {...rest}
    >
      {lead ?? <FileText className="h-3 w-3 text-[var(--fp-ink-3)]" />}
      {clause}
    </button>
  );
});

/** The sources count in a pane header (Gerry's "quiet cluster",
 *  2026-10-06): a quiet button, a stack glyph and the number in the accent,
 *  named "N sources"; nothing when the artifact cites nothing. The header
 *  leaves it out below the phone tier (``rules/frontend.md``): the header
 *  there fits close, the name and the action cluster and nothing else, and
 *  the chips on the content carry the citations. */
export const ArtifactSourcesButton = ({
  sourceMap,
  citationScope,
}: ArtifactSourcesButtonProps) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const sources = useMemo(() => artifactSources(sourceMap), [sourceMap]);
  const clause = evidenceSources(sources.length);
  if (!clause) return null;
  return (
    <>
      <Tooltip
        title={clause}
        arrow
        placement="bottom"
        // The title repeats the name; it must not become a second label.
        describeChild
      >
        <button
          type="button"
          aria-label={clause}
          aria-haspopup="dialog"
          aria-expanded={Boolean(anchor)}
          onClick={(e) => setAnchor(e.currentTarget)}
          className={`flex h-7 flex-none cursor-pointer items-center gap-1 rounded-[7px] px-1.5 text-[13px] font-medium tabular-nums text-[var(--fp-accent)] hover:bg-[var(--fp-hover)] ${
            anchor ? "bg-[var(--fp-hover)]" : ""
          }`}
        >
          <Files className="h-4 w-4" aria-hidden="true" />
          {sources.length}
        </button>
      </Tooltip>
      <SourcesPopover
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        sources={sources}
        citationQueryId={citationScope ?? undefined}
      />
    </>
  );
};

interface CitedCellProps {
  /** The cell's value as its passage is checked against: the stored
   *  value as text, never a display format. */
  text: string;
  /** The value as the reader sees it, when the column formats it (a
   *  currency column's `$60,753.52` for a stored `60753.52`). */
  shown?: string;
  /** The column's type: only number and text values are checked against
   *  their passage, since a reformatted date is not a derived value. */
  columnType: TableColumn["type"];
  /** The bracketed markers the cell cites, in the order stored. */
  markers: string[];
  sourceMap: ArtifactSourceMap | null | undefined;
  /** Chip numbers, from the same derivation that numbers the sources list. */
  numbers: Record<string, number>;
  citationScope?: string | null;
}

type CellCitationTarget = Omit<ChatCitationTarget, "context">;

interface CellCitation {
  marker: string;
  /** The document's chip number, shared with the sources list. */
  number: number;
  /** The document's name, as the citation button names it. */
  label: string;
  /** Everything the citation opens with, bar the surrounding-text thunk. */
  target: CellCitationTarget;
}

/** The distinct citations a cell carries, one per document number, the
 *  first bound to the value itself (a prose cell's led by a source whose
 *  passage holds its text, #14543). The one resolution both the cell's
 *  citation buttons and its menu's Source entries are built from, so the two
 *  cannot open a source differently. */
const cellCitations = ({
  text,
  shown,
  columnType,
  markers,
  sourceMap,
  numbers,
  citationScope,
  keepUnmatched = false,
}: CitedCellProps & {
  /** Keep a document source whose passage does not print the value, opened
   *  on its page with nothing highlighted, rather than dropping it. Only a
   *  deep link to the cell's source asks for it (#13788): the API published
   *  that document as the value's source, so the link lands there. A prose
   *  cell keeps such a source without asking (#14543). */
  keepUnmatched?: boolean;
}): CellCitation[] => {
  const seen = new Set<number>();
  const shownText: string = shown ?? text;
  const checked = columnType === "number" || columnType === "text";
  const prose: boolean =
    columnType === "text" && text.trim() !== "" && !isFigure(text);
  const resolved = markers.flatMap((marker) => {
    const entry = sourceMap?.[marker]?.[0];
    const number = numbers[marker];
    if (!entry || !number) return [];
    // A dataset row (a structured-data source) is not cited in a cell: the
    // underline read as a link to a record, and the pane it opened was blank
    // (Gerry, 2026-10-02). That holds for a row that names a record too: a
    // table links a record through its record column. The artifact keeps the
    // citation, and the header's sources list still names it.
    if (entry.iodlSource) return [];
    // A derived or computed figure has no source (Gerry, 2026-09-28,
    // #12090): a document source whose cited passage does not hold the
    // value is dropped. One with no passage to check stays: an RMS fact, or
    // a whole-document citation, whose content is only the filename.
    // Prose is not a figure (#14543): a text cell that summarizes its
    // clause holds none of the clause's words, so it keeps every document
    // source it cites, and one whose passage lacks its text opens on the
    // passage itself. Only a number cell, and a text cell that is a figure,
    // must find their value in the passage.
    const wholeDocument =
      entry.docLevel === true || WHOLE_DOCUMENT_MARKER.test(marker);
    const passage: string | undefined =
      checked && entry.docId && !wholeDocument
        ? entry.content || undefined
        : undefined;
    const spelling: string | null =
      passage !== undefined ? passageSpelling(text, passage) : text;
    if (spelling === null && !prose && !keepUnmatched) return [];
    return [{ marker, entry, number, spelling }];
  });
  // A prose cell leads with a source whose passage holds its text, so the
  // value opens on itself wherever a page prints it; the order is otherwise
  // the stored one.
  const ordered = prose
    ? [
        ...resolved.filter(({ spelling }) => spelling !== null),
        ...resolved.filter(({ spelling }) => spelling === null),
      ]
    : resolved;
  return ordered.flatMap(({ marker, entry, number, spelling }) => {
    if (seen.has(number)) return [];
    // A prose citation whose passage lacks the text opens on that passage.
    const opensPassage: boolean = spelling === null && prose;
    // The cell's first citation shows its text. An unmatched source opens
    // with no value to look for: a prose cell's on its passage, a figure's
    // (keepUnmatched) on its page.
    const leads: boolean = seen.size === 0;
    seen.add(number);
    const label = chatSourceLabel(entry);
    return [
      {
        marker,
        number,
        label,
        target: {
          displayLabel: label,
          displayText:
            leads && (spelling !== null || prose) ? shownText : undefined,
          // The page may print the value another way (`209.3%` for a cell
          // of `2.093`, #12452), and a currency column shows it as dollars:
          // the viewer looks for the page's spelling.
          highlightText:
            leads && spelling !== null && spelling !== shownText
              ? spelling
              : undefined,
          docId: entry.docId,
          publicId: entry.publicId,
          kind: entry.kind,
          factName: entry.factName,
          interactionId: entry.interactionId,
          artifactId: entry.artifactId,
          // A recorded calculation (#13618) has no document: its value opens
          // the calculation card, and the cell's menu leaves it out.
          calculation:
            chatSourceKind(entry) === "calculation"
              ? chatSourceCalculation(entry, sourceMap ?? {}, new Set([marker]))
              : undefined,
          iodlQid: citationScope ?? undefined,
          pageNum: entry.pageNum ?? undefined,
          sourceContent: entry.content,
          // The value where the page prints it, or nothing (#12090): a
          // cell's passage is its whole source table, and a value the agent
          // reworded, converted or computed is on no page at all. A prose
          // cell the passage does not hold tints the passage instead: its
          // passage is the clause it summarizes (#14543).
          exactOnly: !opensPassage,
          passageOnly: opensPassage,
          fromArtifact: true,
        },
      },
    ];
  });
};

/** A whole-document citation marker, `[NNNN.0]`, as the backend's
 *  `_is_document_level_marker` reads it (#10684). */
const WHOLE_DOCUMENT_MARKER = /^\[\d{4,7}\.0\]$/;

/** A cited table cell, rendered the way a cited figure in a chat reply is
 *  (Gerry, 2026-09-09): the value itself is the citation, no trailing
 *  circle, dotted underline, and opens its source on click. The underline
 *  came back once derived figures stopped being cited (Gerry, 2026-09-28,
 *  #12090; #12100 had dropped it); prose keeps its sources (#14543). A
 *  cell that cites a second, different source gets that one as a numbered
 *  chip after the value. */
export const CitedCell = (props: CitedCellProps) => {
  const citations = cellCitations(props);
  if (citations.length === 0) return <>{props.shown ?? props.text}</>;
  return (
    <>
      {citations.map(({ marker, number, target }) => (
        <ChatCitationButton key={marker} displayNumber={number} {...target} />
      ))}
    </>
  );
};

interface CitedCellSource {
  label: string;
  open: () => void;
}

/** The cell's sources for its menu, which has no button to click: each
 *  opens with the target its citation button carries. No surrounding text:
 *  a cell holds nothing but its citations, so the button's is empty too. */
export const citedCellSources = (props: CitedCellProps): CitedCellSource[] =>
  cellCitations(props)
    // A calculation has no source to open from a menu: the cell's value
    // opens its card (#13618), and an entry here would do nothing.
    .filter(({ target }) => !target.calculation)
    .map(({ label, target }) => ({
      label,
      open: () => openChatCitation(target),
    }));

/** The source a deep link to a cell opens (#13788): the cell's citation for
 *  that one marker, opened as its Source menu entry opens it — beside the
 *  table, on the cited page, the value highlighted where the page prints
 *  it. A source whose passage does not print the value (a converted or
 *  computed figure, which the menu leaves out, #12090) still opens, on its
 *  page with nothing highlighted; a prose cell's opens on its passage
 *  (#14543). Null when the marker opens no document the cell can cite. */
export const linkedCellSource = (
  props: Omit<CitedCellProps, "markers"> & { marker: string }
): CitedCellSource | null => {
  const [citation] = cellCitations({
    ...props,
    markers: [props.marker],
    keepUnmatched: true,
  });
  if (!citation) return null;
  return {
    label: citation.label,
    open: () => openChatCitation(citation.target),
  };
};

/** Chip numbers for an artifact's map — one derivation for chips and list. */
export const artifactCitationNumbers = (
  sourceMap: ArtifactSourceMap | null | undefined
): Record<string, number> => citationNumbers(artifactSources(sourceMap));
